import { FC, createContext, useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { useUser } from "auth";
import { useAuthContext } from "./AuthContext";

interface EventParams {
  label?: string;
  eventId?: string;
  artistName?: string;
}

interface LogClickParams extends EventParams {
  action: string;
}

interface LogPaymentSuccessParams extends EventParams {
  label: string;
  value?: number;
}

interface LogLeadFormSubmission extends EventParams {
  label: string;
}

interface ContextInterface {
  logClick: (logClickParams: LogClickParams) => void;
  logPaymentSuccess: (logPaymentSuccessParams: LogPaymentSuccessParams) => void;
  logLeadFormSubmission: (
    logLeadFormSubmissionParams: LogLeadFormSubmission
  ) => void;
}

export const AnalyticsContext = createContext<ContextInterface>(null);

export const AnalyticsProvider: FC = ({ children }) => {
  const { user } = useUser();
  const { isArtist } = useAuthContext();

  useEffect(() => {
    TagManager.initialize({
      gtmId: process.env.REACT_APP_GTM_ID || "GTM-KLZ7VPV",
    });
  }, []);

  useEffect(() => {
    if (user) {
      setUserId(user.uid);
    }
  }, [user]);

  useEffect(() => {
    if (isArtist) {
      setUserIsArtist();
    }
  }, [isArtist]);

  const setUserId = (uid: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "set_user_id",
        user_id: uid,
      },
    });
  };

  const setUserIsArtist = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: "set_user_is_artist",
        user_is_artist: true,
      },
    });
  };

  const logClick = ({ eventId, action, label, artistName }: LogClickParams) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "button_click",
        action,
        label,
        event_id: eventId,
        artist_name: artistName,
      },
    });
  };

  const logPaymentSuccess = ({
    eventId,
    label,
    artistName,
  }: LogPaymentSuccessParams) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "payment_success",
        label,
        event_id: eventId,
        artist_name: artistName,
      },
    });
  };

  const logLeadFormSubmission = ({
    eventId,
    label,
    artistName,
  }: LogLeadFormSubmission) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "lead_gen",
        label,
        event_id: eventId,
        artist_name: artistName,
      },
    });
  };

  const providerValue: ContextInterface = {
    logClick,
    logPaymentSuccess,
    logLeadFormSubmission,
  };

  return (
    <AnalyticsContext.Provider value={providerValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};
